import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Clock, 
  RefreshCw,
  ExternalLink,
  Info,
  Key,
  Calendar,
  Users,
  Settings
} from 'lucide-react';
import { useQuery } from 'react-query';
import { apiClient } from '../lib/api';
import toast from 'react-hot-toast';

interface LicenseInfo {
  installation: {
    installationId: string;
    licenseServerUrl: string | null;
    offlineGraceHours: number;
    lastLicenseCheck: string | null;
    licenseStatus: string | null;
    platform: string;
    nodeVersion: string;
    version: string;
  };
  license: {
    status: string | null;
    info: any;
    lastCheck: string | null;
    isWithinGracePeriod: boolean;
  };
  timestamp: string;
}

interface LicenseStatusProps {
  showDetails?: boolean;
  onStatusChange?: (status: string) => void;
}

export default function LicenseStatus({ showDetails = false, onStatusChange }: LicenseStatusProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Lizenzstatus abrufen
  const { 
    data: licenseData, 
    isLoading, 
    error, 
    refetch 
  } = useQuery<LicenseInfo>(
    'licenseStatus',
    async () => {
      const response = await apiClient.get('/license/status');
      return response.data.data;
    },
    {
      refetchInterval: 5 * 60 * 1000, // Alle 5 Minuten aktualisieren
      refetchOnWindowFocus: true,
      retry: 2,
      onError: (error: any) => {
        console.error('❌ Fehler beim Abrufen des Lizenzstatus:', error);
        toast.error('Lizenzstatus konnte nicht abgerufen werden');
      }
    }
  );

  // Lizenzstatus an Parent-Komponente weiterleiten
  useEffect(() => {
    if (licenseData && onStatusChange) {
      const status = licenseData.license.status || 'unknown';
      onStatusChange(status);
    }
  }, [licenseData, onStatusChange]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      toast.success('Lizenzstatus aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Lizenzstatus');
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusIcon = (status: string | null | undefined) => {
    switch (status) {
      case 'valid':
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'expired':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'suspended':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'offline_grace_period':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'no_license_server':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string | null | undefined) => {
    switch (status) {
      case 'valid':
      case 'active':
        return 'Lizenziert';
      case 'expired':
        return 'Abgelaufen';
      case 'suspended':
        return 'Suspendiert';
      case 'offline_grace_period':
        return 'Offline-Grace-Periode';
      case 'no_license_server':
        return 'Kein Lizenzserver';
      case 'installation_not_found':
        return 'Nicht registriert';
      default:
        return 'Unbekannt';
    }
  };

  const getStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case 'valid':
      case 'active':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'expired':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'suspended':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'offline_grace_period':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'no_license_server':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Nie';
    return new Date(dateString).toLocaleString('de-DE');
  };

  if (isLoading && !licenseData) {
    return (
      <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border">
        <RefreshCw className="w-4 h-4 animate-spin text-gray-500" />
        <span className="text-sm text-gray-600">Lizenzstatus wird geladen...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
        <div className="flex items-center space-x-2">
          <XCircle className="w-5 h-5 text-red-500" />
          <span className="text-sm text-red-600">Lizenzstatus konnte nicht geladen werden</span>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
    );
  }

  const status = licenseData?.license.status;
  const installation = licenseData?.installation;

  return (
    <div className="space-y-3">
      {/* Hauptstatus */}
      <div className={`flex items-center justify-between p-3 rounded-lg border ${getStatusColor(status)}`}>
        <div className="flex items-center space-x-3">
          {getStatusIcon(status)}
          <div>
            <div className="font-medium text-sm">
              {getStatusText(status)}
            </div>
            {installation?.licenseServerUrl && (
              <div className="text-xs opacity-75">
                Installation: {installation.installationId.substring(0, 8)}...
              </div>
            )}
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-1 hover:bg-white/50 rounded disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Details */}
      {showDetails && licenseData && (
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-gray-600" />
            <h3 className="font-medium text-gray-900">Lizenz-Details</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Installation-Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                <Settings className="w-4 h-4" />
                <span>Installation</span>
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">ID:</span>
                  <span className="font-mono text-xs">{installation?.installationId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Version:</span>
                  <span>{installation?.version}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plattform:</span>
                  <span>{installation?.platform}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Letzte Prüfung:</span>
                  <span>{formatDate(installation?.lastLicenseCheck)}</span>
                </div>
              </div>
            </div>

            {/* Lizenz-Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                <Key className="w-4 h-4" />
                <span>Lizenz</span>
              </h4>
              <div className="space-y-2 text-sm">
                {licenseData.license.info && (
                  <>
                    {licenseData.license.info.licenseType && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Typ:</span>
                        <span className="capitalize">{licenseData.license.info.licenseType}</span>
                      </div>
                    )}
                    {licenseData.license.info.expiresAt && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ablaufdatum:</span>
                        <span>{formatDate(licenseData.license.info.expiresAt)}</span>
                      </div>
                    )}
                    {licenseData.license.info.schoolName && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Schule:</span>
                        <span>{licenseData.license.info.schoolName}</span>
                      </div>
                    )}
                    {licenseData.license.info.maxUsers && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max. Benutzer:</span>
                        <span className="flex items-center space-x-1">
                          <Users className="w-3 h-3" />
                          <span>{licenseData.license.info.maxUsers === -1 ? '∞' : licenseData.license.info.maxUsers}</span>
                        </span>
                      </div>
                    )}
                  </>
                )}
                {licenseData.license.isWithinGracePeriod && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Grace-Periode:</span>
                    <span className="text-yellow-600">Aktiv</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Features */}
          {licenseData.license.info?.features && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Verfügbare Features</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(licenseData.license.info.features).map(([feature, enabled]) => (
                  <div
                    key={feature}
                    className={`flex items-center space-x-2 p-2 rounded text-xs ${
                      enabled 
                        ? 'bg-green-50 text-green-700 border border-green-200' 
                        : 'bg-gray-50 text-gray-500 border border-gray-200'
                    }`}
                  >
                    {enabled ? (
                      <CheckCircle className="w-3 h-3" />
                    ) : (
                      <XCircle className="w-3 h-3" />
                    )}
                    <span className="capitalize">{feature.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lizenzserver-Info */}
          {installation?.licenseServerUrl && (
            <div className="pt-3 border-t">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Lizenzserver: {installation.licenseServerUrl}</span>
                <button
                  onClick={() => installation.licenseServerUrl && window.open(installation.licenseServerUrl, '_blank')}
                  className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="w-3 h-3" />
                  <span>Öffnen</span>
                </button>
              </div>
            </div>
          )}

          {/* Warnung bei ungültiger Lizenz */}
          {status && !['valid', 'active', 'no_license_server'].includes(status) && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Lizenz-Warnung</p>
                  <p className="mt-1">
                    Ihre Lizenz ist derzeit nicht gültig. Bitte kontaktieren Sie Ihren Administrator 
                    oder wenden Sie sich an den Support.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
